import React, { useState, useEffect, useCallback } from 'react';
import './SecretsModal.css';
import authService from '../services/authService';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const SERVER_URL = window.location.hostname.includes('devtunnels.ms') 
  ? `https://${window.location.hostname}/api` 
  : '/api';

const SecretsModal = ({ isOpen, onClose }) => {
  useModalSelectionGuard(isOpen);

  const [formData, setFormData] = useState({
    bcsSecret: '',
    finamSecret: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Очищаем сообщения об ошибках и успехе при вводе
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleCancel = useCallback(() => {
    setFormData({
      bcsSecret: '',
      finamSecret: ''
    });
    setError(null);
    setSuccess(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const promises = [];

      // Если заполнен секрет БКС, отправляем запрос
      if (formData.bcsSecret.trim()) {
        promises.push(
          authService.authenticatedRequest(`${SERVER_URL}/v1/users/bcs-secret`, {
            method: 'POST',
            body: JSON.stringify({ secret: formData.bcsSecret.trim() })
          })
        );
      }

      // Если заполнен секрет Финам, отправляем запрос
      if (formData.finamSecret.trim()) {
        promises.push(
          authService.authenticatedRequest(`${SERVER_URL}/v1/users/finam-secret`, {
            method: 'POST',
            body: JSON.stringify({ secret: formData.finamSecret.trim() })
          })
        );
      }

      // Если ничего не заполнено
      if (promises.length === 0) {
        setError('Заполните хотя бы одно поле');
        setIsLoading(false);
        return;
      }

      // Выполняем все запросы параллельно
      await Promise.all(promises);

      setSuccess('Секреты успешно сохранены');
      
      // Очищаем форму и закрываем модальное окно через 1 секунду
      setTimeout(() => {
        setFormData({
          bcsSecret: '',
          finamSecret: ''
        });
        setSuccess(null);
        onClose();
      }, 1000);

    } catch (err) {
      setError(err.message || 'Ошибка при сохранении секретов');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Настройка профиля</h2>
          <button className="secrets-close-btn" onClick={handleCancel}>×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <label htmlFor="bcsSecret">Токен БКС (с правами на торговлю)</label>
            <input
              type="text"
              id="bcsSecret"
              name="bcsSecret"
              value={formData.bcsSecret}
              onChange={handleInputChange}
              placeholder="Введите refresh-токен БКС"
              disabled={isLoading}
            />
            <a
              href="https://trade-api.bcs.ru/#%D1%81-%D1%87%D0%B5%D0%B3%D0%BE-%D0%BD%D0%B0%D1%87%D0%B0%D1%82%D1%8C"
              target="_blank"
              rel="noopener noreferrer"
              className="bcs-secret-instruction-link"
            >
              Инструкция получения токена
            </a>
          </div>

          <div className="form-group">
            <label htmlFor="finamSecret">Токен Финам</label>
            <input
              type="text"
              id="finamSecret"
              name="finamSecret"
              value={formData.finamSecret}
              onChange={handleInputChange}
              placeholder="Введите API-токен Финам"
              disabled={isLoading}
            />
            <a
              href="https://tradeapi.finam.ru/docs/tokens"
              target="_blank"
              rel="noopener noreferrer"
              className="finam-secret-instruction-link"
            >
              Инструкция получения токена
            </a>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="secrets-modal-buttons">
            <button 
              type="button" 
              className="secrets-cancel-btn"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Отмена
            </button>
            <button 
              type="button" 
              className="secrets-save-btn"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecretsModal;
